<template>
    <BarCard>
        <template slot="header">
            <div class="plan-header">
                <div class="plan-header-text">
                    <span class="plan-name">{{ plan.planName }}</span>
                </div>
                <span v-if="plan.topParent" class="offer-name">{{ plan.topParent.name }}</span>
                <span
                    class="plan-title-info"
                    tabindex="0"
                    aria-label="Plan eligibility information"
                >
                    <i class="fas fa-info-circle"></i>
                    <span class="plan-eligibility-tooltip" role="tooltip">{{ planEligibilityTooltip }}</span>
                </span>
            </div>
        </template>
        <template slot="body">
            <div>
                <slot name="header"></slot>
            </div>

            <div v-if="isPsr && canContinueAsMember" class="psr-continue-as-member">
                <button type="button" class="btn-add-to-cart" @click.prevent="continueAsMemberToSelectPlan">
                    Continue as member to select this plan
                </button>
            </div>
            <div v-if="!hidePlanPrice" class="member-selection-area">
                <div v-if="hasApplicableDiscounts" class="discount-notice">
                    <i class="fas fa-tag"></i> Discounts available for multiple family member selection
                </div>

                <template v-if="hasEligibleFamilyMembers">
                    <div class="members-table-wrap">
                        <table class="members-table">
                            <tbody>
                                <tr v-for="(member, index) in eligibleFamilyMembers" :key="member.consumerId" v-if="findMemberPrice(member) != null">
                                    <td class="member-name">{{ member.firstName }} {{ member.lastName }}</td>
                                    <td>
                                        <select
                                            class="frequency-select"
                                            :value="getEffectiveFrequency(member)"
                                            @change="setMemberFrequency(member, Number($event.target.value))"
                                        >
                                            <option
                                                v-for="freq in planFrequencies"
                                                :key="'member-freq-' + member.consumerId + '-' + freq.frequencyId"
                                                :value="freq.monthFrequency"
                                            >
                                                {{ freq.monthFrequency > 1 ? `${freq.monthFrequency}-month` : 'Monthly' }}
                                            </option>
                                        </select>
                                    </td>
                                    <td class="member-price-cell">
                                        <span v-if="showPricing" class="member-price-amount">
                                            {{ (findMemberPrice(member) * getEffectiveFrequency(member)) | currency }}
                                        </span>
                                    </td>
                                    <td class="member-cart-cell">
                                        <div class="member-cart-action">
                                            <template v-if="plan.familyPlan && index === 0">
                                                <span v-if="isMemberPlanInCart(member)" class="in-cart-badge">
                                                    <a href="#" @click.prevent="viewCartInPanel()" class="cart-view-link wl-interactive">
                                                        <span class="in-cart-label">Plan In Cart</span>
                                                        <i class="fa fa-check-circle in-cart-check-icon" aria-hidden="true"></i>
                                                    </a>
                                                </span>
                                                <button v-else @click.prevent="addFamilyPlan()" type="button" class="btn-add-to-cart">
                                                    Add Family Plan
                                                </button>
                                            </template>
                                            <template v-else-if="!plan.familyPlan">
                                                <span v-if="isMemberPlanInCart(member)" class="in-cart-badge">
                                                    <a href="#" @click.prevent="viewCartInPanel()" class="cart-view-link wl-interactive">
                                                        <span class="in-cart-label">Plan In Cart</span>
                                                        <i class="fa fa-check-circle in-cart-check-icon" aria-hidden="true"></i>
                                                    </a>
                                                </span>
                                                <button v-else @click.prevent="addPlan(member)" type="button" class="btn-add-to-cart">
                                                    Add to Cart
                                                </button>
                                            </template>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
                <template v-else>
                    <p class="section-label">Membership Options</p>
                    <div v-if="planFrequencies.length" class="billing-frequency">
                        <div v-for="freq in planFrequencies" :key="freq.frequencyId" class="form-check-inline">
                            <input
                                @change="onChange(freq.monthFrequency)"
                                class="form-check-input"
                                type="radio"
                                :name="'frequencyChoice' + plan.planId"
                                :id="freq.frequencyId"
                                :checked="currentFrequency == freq.monthFrequency"
                            >
                            <label class="form-check-label" :for="freq.frequencyId">
                                <template v-if="freq.monthFrequency > 1">{{ freq.monthFrequency }}-month</template>
                                <template v-else>Monthly</template>
                            </label>
                        </div>
                    </div>
                    <div v-if="showPricing" class="price-range">
                        <template v-if="prices.isSamePrice">
                            {{ prices.lowestPlanPrice | currency }}/mo
                        </template>
                        <template v-else>
                            {{ prices.lowestPlanPrice | currency }}/mo - {{ prices.highestPlanPrice | currency }}/mo
                        </template>
                    </div>
                </template>
            </div>

            <div class="view-details-toggle">
                <a href="#" @click.prevent="showDetails = !showDetails" class="details-link">
                    <!-- <span class="details-link-text">{{ showDetails ? 'Hide Details' : 'View Details' }}</span> -->
                    <span class="details-link-text">Details</span>
                    <i :class="showDetails ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="details-chevron"></i>
                </a>
            </div>

            <div v-show="showDetails" class="plan-details">
                <div class="plan-details-section">
                    <p class="section-label">Features</p>
                    <plan-settings :plan="plan" :showOnlyOffered="true"></plan-settings>
                </div>
                <div v-if="plan.otherServices" class="plan-details-section">
                    <p class="section-label">Other Services</p>
                    <span>{{ plan.otherServices }}</span>
                </div>
                <upcoming-plan-changes :planId="plan.planId"></upcoming-plan-changes>
            </div>

            <slot></slot>
        </template>
    </BarCard>
</template>

<script>
    import PlanSettings from "./PlanSettings.vue";
    import UpcomingPlanChanges from "./UpcomingPlanChanges.vue";
    import BarCard from "@/pages/member/components/dashboard/BarCard.vue";
    import {api} from "../../../global/framework/api.js";
    import {mapGetters} from "vuex";
    import {planPricesMixin} from "../../../components/shared/mixins/planPricesMixin.js";
    import {notifications} from "../../../global/notifications.js";
    import {common} from "../../../global/common.js";
    import {impersonationHelper} from "../../../global/impersonation.js";

    export default {
        name: "Plan",
        components: {UpcomingPlanChanges, PlanSettings, BarCard},
        props: {
            plan: {type: [Object], required: true},
            selectPlan: {type: [Boolean], required: false, default: null},
            providerEntityId: {type: [Number], required: true, default: null},
            hidePlanPrice: {type: [Boolean], required: false, default: false},
            configFrequencies: {type: [Array], required: false, default: null}
        },
        mixins: [planPricesMixin],
        data() {
            return {
                discountsData: [],
                widgetReady: false,
                prices: {},
                planFrequencies: [],
                currentFrequency: 1,
                buttonText: "Add to Cart",
                selectedMembers: [],
                primaryId: null,
                showDetails: false,
                memberFrequencies: {},
            };
        },
        computed: {
            ...mapGetters(['hasPendingSelections']),
            ...mapGetters({
                showPricing: "globalSettings/showPricing",
                isPsr: "globalSettings/isPsr",
                canEdit: "globalSettings/canEdit",
            }),
            canContinueAsMember() {
                return this.canEdit && this.$route && this.$route.params.consultantId && this.$route.params.consumerId;
            },
            ...mapGetters(["itemsInCart"]),
            ...mapGetters(['familyMembers']),
            hasApplicableDiscounts() {
                if(this.discountsData == null) return false;
                if(this.plan.familyCost === null || this.plan.familyCost === undefined){
                    return this.discountsData.length > 0;
                }
                else
                {
                    let count = 0;
                    for (let cost of this.plan.familyCost){
                        if(cost.price != null){
                            count++;
                        }
                    }
                    return this.discountsData.length > 0 && count > 1;
                }
            },
            eligibleFamilyMembers() {
                if (!this.familyMembers || !this.familyMembers.length) {
                    return [];
                }
                return this.familyMembers.filter(x => !x.effEndDate || x.effEndDate > new Date());
            },
            hasEligibleFamilyMembers() {
                if (this.isPsr) {
                    return false;
                }
                return this.eligibleFamilyMembers.length > 0;
            },
            isCustomPlan() {
                return this.plan.openTemplate || this.plan.fileId;
            },
            planEligibilityTooltip() {
                return "Members may not be eligible for a variety of reasons including age of member, plan settings, etc.";
            }
        },
        methods: {
            continueAsMemberToSelectPlan() {
                const consultantId = Number(this.$route.params.consultantId);
                const consumerId = Number(this.$route.params.consumerId);
                const providerEntityId = this.providerEntityId || Number(this.$route.params.providerEntityId);
                const planCategoryId = this.plan && this.plan.planCategoryId;
                let url = `/member/#/${consumerId}/providerProfile/${providerEntityId}`;
                if (planCategoryId) {
                    url += `?planCategoryId=${planCategoryId}`;
                }
                impersonationHelper.impersonateAsMember(consultantId, consumerId, url);
            },
            async getFrequencies() {
                if (!Array.isArray(this.configFrequencies) || !Array.isArray(this.plan.monthFrequencies)) {
                    return;
                }
                this.planFrequencies = this.plan.monthFrequencies;
                for (let freq of this.planFrequencies) {
                    const configFrequency = this.configFrequencies.find(x => x.planFrequencyId === freq.planFrequencyId);
                    if (!configFrequency) {
                        continue;
                    }
                    freq.monthFrequency = configFrequency.monthFrequency;
                    freq.frequencyId = this.getFrequencyId(freq.monthFrequency);
                }
                this.planFrequencies.sort((a, b) => a.monthFrequency - b.monthFrequency);
                if (this.planFrequencies.length && this.planFrequencies[0].monthFrequency > 0) {
                    this.currentFrequency = this.planFrequencies[0].monthFrequency;
                }
            },
            getFrequencyId(id) {
                return "frequency-" + id + "-" + this.plan.planId
            },
            onChange(frequency) {
                this.currentFrequency = frequency
            },
            getEffectiveFrequency(member) {
                if (this.plan.familyPlan) {
                    return this.currentFrequency;
                }
                return this.memberFrequencies[member.consumerId] || this.currentFrequency;
            },
            setMemberFrequency(member, frequency) {
                this.$set(this.memberFrequencies, member.consumerId, frequency);
            },
            addPlan(member) {
                this.selectedMembers = [member.consumerId]
                this.validateSelection(member, false, this.getEffectiveFrequency(member));
            },
            addFamilyPlan(){
                for (let member of this.eligibleFamilyMembers.filter(x => !x.effEndDate)){
                    this.selectedMembers.push(member.consumerId);
                }
                this.validateSelection(null, true, this.currentFrequency);
            },
            async validateSelection(member, isFamilyPlan, frequency) {
                if (!this.selectedMembers.length) {
                    notifications.show({title: "Error", message: "No plans are selected"}, common.ACRESSA_NOTIFICATIONS.ERROR);
                    return;
                }
                await this.$store.dispatch("ADD_ITEMS_TO_CART", {
                    mainConsumerId: this.primaryId,
                    consumerIds: this.selectedMembers,
                    planId: this.plan.planId,
                    providerEntityId: this.providerEntityId,
                    monthFrequency: frequency !== undefined ? frequency : this.currentFrequency,
                    planCategoryId: this.plan.planCategoryId,
                    planName: this.plan.planName,
                    isFamilyPlan,
                });
            },
            viewCartInPanel() {
                if (this.$bus && this.$AcEvents && this.$AcEvents.OPEN_CART_PANEL) {
                    this.$bus.$emit(this.$AcEvents.OPEN_CART_PANEL);
                    return;
                }
                this.$router.push({name: "checkout", params: {consumerId: this.primaryId}});
            },
            isMemberPlanInCart(member) {
                for (let cartItem of this.itemsInCart) {
                    if (cartItem.consumerId === member.consumerId && cartItem.planId === this.plan.planId && cartItem.providerEntityId === this.providerEntityId) {
                        return true
                    }
                }
                return false
            }
        },
        async mounted() {
            let dd = await api.getDataById('Shared', 'GetPlanDiscounts', this.plan.planId)
            await this.getFrequencies();
            this.discountsData = dd;
            if (!this.hasEligibleFamilyMembers) {
                this.prices = this.getPlanPriceRange(this.plan.priceTiers);
            } else {
                let member = this.eligibleFamilyMembers[0];
                this.primaryId = member.isDependent ? member.primaryId : member.consumerId;
            }
            this.widgetReady = true;
        },
    }
</script>

<style scoped>
    .plan-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        overflow: visible;
    }

    .plan-header-text {
        display: flex;
        flex-direction: column;
    }

    .plan-name {
        font-size: 1.15rem;
        font-weight: 700;
    }

    .offer-name {
        margin-left: auto;
        text-align: right;
        font-size: 1.15rem;
        font-weight: 400;
        color: #3e3f40 !important;
    }

    .plan-title-info {
        position: relative;
        margin-left: 3px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        cursor: help;
        color: var(--bs-success, #198754) !important;
    }

    .plan-title-info i {
        color: var(--bs-success, #198754) !important;
    }

    .plan-eligibility-tooltip {
        visibility: hidden;
        opacity: 0;
        position: absolute;
        right: 0;
        bottom: calc(100% + 8px);
        z-index: 1060;
        width: max-content;
        max-width: min(280px, 90vw);
        padding: 8px 12px;
        border-radius: 4px;
        background: #333;
        color: #fff !important;
        font-size: 0.85rem;
        font-weight: 400;
        line-height: 1.4;
        text-align: left;
        white-space: normal;
        pointer-events: none;
        transition: opacity 0.15s ease;
    }

    .plan-title-info:hover .plan-eligibility-tooltip,
    .plan-title-info:focus .plan-eligibility-tooltip {
        visibility: visible;
        opacity: 1;
    }

    .plan-template {
        overflow: hidden;
    }

    .discount-notice {
        background: #e8f4fd;
        color: #0c5460 !important;
        padding: 8px 14px;
        border-radius: 6px;
        font-size: 0.85rem;
        margin-bottom: 12px;
    }

    .discount-notice i {
        color: #0c5460 !important;
    }

    .member-selection-area {
        margin-bottom: 8px;
    }

    .billing-frequency {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        flex-wrap: wrap;
    }

    .freq-label {
        font-weight: 600;
        min-width: 52px;
    }

    .form-check-inline {
        margin-right: 0;
        margin-bottom: 0;
    }

    .form-check-input {
        margin-top: 4px;
        margin-right: 4px;
    }

    .form-check-label {
        margin-bottom: 0;
        padding-left: 2px;
    }

    .members-table-wrap {
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
        overflow-x: auto;
    }

    .members-table {
        width: 100%;
        border-collapse: collapse;
    }

    .members-table td {
        padding: 10px 8px;
        border-bottom: 1px solid #e9ecef;
        vertical-align: middle;
        text-align: left;
    }

    .members-table td:nth-child(2) {
        text-align: center;
    }

    .member-price-cell,
    .member-cart-cell {
        text-align: right !important;
    }

    .member-price-cell {
        white-space: nowrap;
    }

    .member-name {
        font-weight: 700;
    }

    .member-price-amount {
        font-weight: 600;
        white-space: nowrap;
    }

    .frequency-select {
        padding: 4px 8px;
        border: 1px solid #ced4da;
        border-radius: 6px;
        font-size: 0.85rem;
        background: #fff;
        cursor: pointer;
        color: #3e3f40 !important;
        text-align: center;
        text-align-last: center;
    }

    .member-cart-action {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-shrink: 0;
    }

    .psr-continue-as-member {
        margin: 0 0 12px 0;
    }

    .btn-add-to-cart {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: var(--bs-success, #198754);
        color: #fff !important;
        border: none;
        border-radius: 999px;
        padding: 7px 18px;
        font-size: 0.85rem;
        font-weight: 600;
        cursor: pointer;
        transition: filter 0.15s ease, box-shadow 0.15s ease;
        white-space: nowrap;
    }

    .btn-add-to-cart i {
        color: #fff !important;
    }

    .btn-add-to-cart:hover {
        filter: brightness(1.08);
        box-shadow: 0 2px 8px rgba(25, 135, 84, 0.35);
    }

    .in-cart-badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: var(--bs-success, #198754) !important;
        font-weight: 600;
        font-size: 0.9rem;
        white-space: nowrap;
    }

    .in-cart-badge i {
        color: var(--bs-success, #198754) !important;
    }

    .cart-view-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-weight: bold;
        font-size: 0.85rem;
        color: var(--bs-success, #198754) !important;
    }

    .cart-view-link i {
        color: var(--bs-success, #198754) !important;
    }

    .in-cart-check-icon {
        color: var(--bs-success, #198754) !important;
        font-size: 1.05rem;
        line-height: 1;
    }

    .in-cart-label {
        color: var(--bs-success, #198754) !important;
    }

    .cart-view-link:hover {
        text-decoration: none;
    }

    .cart-view-link,
    .cart-view-link:hover,
    .cart-view-link:focus {
        text-decoration: none !important;
    }

    .cart-view-link *,
    .cart-view-link:hover *,
    .cart-view-link:focus * {
        text-decoration: none !important;
    }

    .price-range {
        font-size: 1.1rem;
        font-weight: 600;
        padding: 8px 0;
    }

    .view-details-toggle {
        padding: 10px 0 4px;
        display: flex;
        justify-content: center;
    }

    .details-link {
        font-size: 1rem;
        font-weight: 600;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--bs-success, #198754) !important;
    }

    .details-link i,
    .details-link-text {
        color: var(--bs-success, #198754) !important;
    }

    .details-link-text {
        font-weight: 700 !important;
    }

    .details-link:hover,
    .details-link:focus,
    .details-link:hover *,
    .details-link:focus * {
        text-decoration: none !important;
    }

    .details-chevron {
        font-size: 1.15rem;
    }

    .plan-details {
        padding-top: 12px;
        border-top: 1px solid #e9ecef;
        margin-top: 18px;
    }

    .plan-details-section {
        margin-bottom: 16px;
    }

    .section-label {
        font-size: 0.95rem;
        font-weight: 700;
        margin: 0 0 6px;
    }

    @media (max-width: 575px) {
        .btn-add-to-cart {
            padding-left: 12px;
            padding-right: 12px;
        }
    }
</style>
